import React, { useState, useEffect } from 'react';
import { loadOrCreateIdentity } from './lib/identity';
import { initSync } from './lib/sync';
import { detectCity } from './lib/location';
import { reseedCachedVideos, reseedFromMagnetURIs } from './lib/torrent';
import { getAllVideos, calculateTastemakerScore } from './lib/metadata';
import { getAllChannelMagnetURIs } from './lib/communities';
import { Upload } from './components/Upload';
import { Feed } from './components/Feed';
import { Profile } from './components/Profile';
import { DirectMessages } from './components/DirectMessages';
import { Communities } from './components/Communities';
import { Tags } from './components/Tags';
import { DiscoverFeed } from './components/DiscoverFeed';
import { Inbox } from './components/Inbox';
import { CreateSheet } from './components/CreateSheet';
import { CommunityDetail } from './components/CommunityDetail';
import { UserProfile as UserProfileView } from './components/UserProfile';
import { Onboarding } from './components/Onboarding';
import { ImportConfirm } from './components/ImportConfirm';
import { NetworkSettings } from './components/NetworkSettings';
import { AdminReports } from './components/AdminReports';
import { isAdmin } from './lib/moderation';
import { parseImportHash } from './lib/deviceTransfer';
import { parseDeepLink, clearDeepLink } from './lib/deepLink';
import { VideoPlayer } from './components/VideoPlayer';
import { PostFeed } from './components/PostFeed';
import { EventFeed } from './components/EventFeed';
import { TastemakerPins } from './components/TastemakerPins';
import { getConversations, addMessageListener } from './lib/messages';
import { Notifications } from './components/Notifications';
import { Legal } from './components/Legal';
import { Live } from './components/Live';
import { getUnreadCount, addNotificationsListener, NotificationRecord } from './lib/notifications';
import { COLORS, SPACING } from './styles/theme';
import { useMobile } from './hooks/useMobile';
import logoWordmark from './assets/logo-wordmark.png';

function App() {
  const [currentView, setCurrentView] = useState<'home' | 'inbox' | 'videos' | 'profile' | 'messages' | 'communities' | 'community-detail' | 'tags' | 'user-profile' | 'settings' | 'video' | 'posts' | 'events' | 'admin-reports' | 'pins' | 'notifications' | 'legal' | 'live' | 'onboarding'>('home');
  const identity = useState(() => loadOrCreateIdentity())[0];
  const [importCode] = useState(() => parseImportHash());
  const [deepLink] = useState(() => parseDeepLink());
  const [importDone, setImportDone] = useState(false);
  const [profileCreated, setProfileCreated] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedVideoHash, setSelectedVideoHash] = useState<string | null>(null);
  const [previousView, setPreviousView] = useState<string>('home');
  const [startConversationWith, setStartConversationWith] = useState<{ userId: string; username: string } | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedCommunityId, setSelectedCommunityId] = useState<string | null>(null);
  const [feedRefresh, setFeedRefresh] = useState(0);
  const [unreadDMs, setUnreadDMs] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const isMobile = useMobile();
  const tastemakerScore = calculateTastemakerScore(identity.userId);

  useEffect(() => {
    if (navigator.storage && navigator.storage.persist) {
      navigator.storage.persist().catch(() => {});
    }

    const isCreated = localStorage.getItem('authenticulture_profile_created');
    loadOrCreateIdentity();
    setProfileCreated(!!isCreated);

    if (deepLink) {
      clearDeepLink();
      if (deepLink.type === 'post') {
        setCurrentView('posts');
      } else if (deepLink.type === 'video') {
        setSelectedVideoHash(deepLink.hash);
        setCurrentView('video');
        setPreviousView('home');
      }
    }

    const DEFAULT_RELAY = 'wss://relay.authenticulture.net';
    const settings = JSON.parse(localStorage.getItem('authenticulture_network_settings') || '{}');
    const userRelays: string[] = settings.relayUrls || [];
    const relayUrls = Array.from(new Set([DEFAULT_RELAY, ...userRelays]));
    initSync(relayUrls);

    reseedCachedVideos(getAllVideos());
    reseedFromMagnetURIs(getAllChannelMagnetURIs());

    // Fetch relay magnet index so we reseed content others uploaded
    const RELAY_HTTP = 'https://relay.authenticulture.net/magnets';
    fetch(RELAY_HTTP)
      .then(r => r.json())
      .then(({ magnets }) => { if (Array.isArray(magnets)) reseedFromMagnetURIs(magnets); })
      .catch(() => {});
    detectCity().catch(() => {});

    const refreshUnread = () => {
      const identity = loadOrCreateIdentity();
      const total = getConversations(identity.userId).reduce((s, c) => s + c.unread, 0);
      setUnreadDMs(total);
    };
    refreshUnread();
    const unsubMessages = addMessageListener(refreshUnread);

    const refreshUnreadNotifications = () => setUnreadNotifications(getUnreadCount());
    refreshUnreadNotifications();
    const unsubNotifications = addNotificationsListener(refreshUnreadNotifications);

    return () => {
      unsubMessages();
      unsubNotifications();
    };
  }, []);

  if (importCode && !importDone) {
    return <ImportConfirm prefillCode={importCode} onImported={() => setImportDone(true)} onCancel={() => setImportDone(true)} />;
  }

  const handleLogoClick = () => {
    setCurrentView('home');
    setSelectedUserId(null);
  };

  const handleViewUserProfile = (userId: string) => {
    setSelectedUserId(userId);
    setCurrentView('user-profile');
  };

  const handleViewTag = (_tag: string) => {
    setCurrentView('tags');
  };

  const handleViewVideo = (hash: string) => {
    setPreviousView(currentView);
    setSelectedVideoHash(hash);
    setCurrentView('video');
  };

  const handleMessageUser = (userId: string, username: string) => {
    setStartConversationWith({ userId, username });
    setCurrentView('messages');
  };

  const handleCreateClick = () => {
    if (!profileCreated) { setCurrentView('onboarding'); return; }
    setShowCreate(true);
  };

  const handleOpenCommunity = (communityId: string) => {
    setSelectedCommunityId(communityId);
    setCurrentView('community-detail');
  };

  const handleNotificationNavigate = (n: NotificationRecord) => {
    if (n.type === 'dm' && n.fromUserId) {
      setStartConversationWith({ userId: n.fromUserId, username: n.fromUsername || n.fromUserId });
      setCurrentView('messages');
    } else {
      setCurrentView('posts');
    }
  };

  const navButton = (label: string, view: string, mobileLabel?: string) => {
    const active = currentView === view;
    if (isMobile) {
      const [icon, sub] = (mobileLabel || label).split('\n');
      return (
        <button
          key={view}
          onClick={() => setCurrentView(view as any)}
          style={{
            flex: 1,
            minWidth: 0,
            padding: `${SPACING.sm} 2px`,
            backgroundColor: 'transparent',
            color: active ? COLORS.accent : COLORS.textMuted,
            border: 'none',
            borderTop: `2px solid ${active ? COLORS.accent : 'transparent'}`,
            cursor: 'pointer',
            fontFamily: '"IBM Plex Mono", monospace',
            fontWeight: active ? 700 : 400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            userSelect: 'none',
          }}
        >
          <span style={{ fontSize: '28px', lineHeight: 1 }}>{icon}</span>
          <span style={{ fontSize: '12px', lineHeight: 1 }}>{sub}</span>
        </button>
      );
    }
    return (
      <button
        onClick={() => setCurrentView(view as any)}
        style={{
          padding: `${SPACING.sm} ${SPACING.lg}`,
          backgroundColor: active ? COLORS.accent : COLORS.panel,
          color: active ? '#fff' : COLORS.text,
          border: `2px outset ${active ? COLORS.accent : COLORS.highlight}`,
          cursor: 'pointer',
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '0.05em',
          transition: 'all 0.05s ease',
          userSelect: 'none',
          textDecoration: 'underline',
          boxShadow: active ? 'inset 1px 1px 0 rgba(255,255,255,0.2)' : 'inset 0 0 0 1px rgba(0,0,0,0.5)'
        }}
        onMouseDown={(e) => {
          e.currentTarget.style.boxShadow = 'inset 1px 1px 2px rgba(0,0,0,0.8)';
          e.currentTarget.style.borderStyle = 'inset';
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.boxShadow = active ? 'inset 1px 1px 0 rgba(255,255,255,0.2)' : 'inset 0 0 0 1px rgba(0,0,0,0.5)';
          e.currentTarget.style.borderStyle = 'outset';
        }}
      >
        {label}
      </button>
    );
  };

  const bottomNav = (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: COLORS.panel,
      borderTop: `1px solid ${COLORS.border}`,
      display: 'flex',
      alignItems: 'stretch',
      height: 78,
      zIndex: 100,
      paddingBottom: 'env(safe-area-inset-bottom)',
    }}>
      {navButton('⌂\nfeed', 'home', '⌂\nfeed')}
      <div style={{ flex: 1, position: 'relative' }}>
        {navButton('✉\ninbox', 'inbox', '✉\ninbox')}
        {unreadDMs > 0 && (
          <span style={{
            position: 'absolute', top: 6, right: 6,
            backgroundColor: COLORS.accent, color: '#fff',
            borderRadius: '50%', width: 18, height: 18,
            fontSize: '10px', fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
          }}>
            {unreadDMs > 9 ? '9+' : unreadDMs}
          </span>
        )}
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <button
          onClick={handleCreateClick}
          aria-label="create"
          style={{
            width: 52, height: 52, borderRadius: '50%',
            background: COLORS.accent, color: '#fff',
            border: `2px outset ${COLORS.highlight}`,
            boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
            fontSize: '30px', fontWeight: 700, lineHeight: 1,
            fontFamily: '"IBM Plex Mono", monospace', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          +
        </button>
      </div>
      <div style={{ flex: 1, position: 'relative' }}>
        {navButton('○\nme', 'profile', '○\nme')}
        <button
          onClick={e => { e.stopPropagation(); setCurrentView('pins'); }}
          style={{
            position: 'absolute', top: 0, left: 0, right: 0,
            background: COLORS.accent, color: '#fff', border: 'none',
            fontFamily: '"IBM Plex Mono", monospace', fontSize: '10px', fontWeight: 700,
            padding: '2px 0', cursor: 'pointer', textAlign: 'center',
            letterSpacing: '0.02em',
          }}
        >
          ★ {tastemakerScore}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{
      backgroundColor: COLORS.bg,
      color: COLORS.text,
      height: '100dvh',
      fontFamily: '"IBM Plex Mono", monospace',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Titlebar */}
      <div style={{
        backgroundColor: COLORS.accent,
        color: '#fff',
        padding: `${SPACING.xs} ${SPACING.sm}`,
        borderBottom: `2px solid ${COLORS.highlight}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '11px',
        fontWeight: 700,
        userSelect: 'none',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACING.xs }}>
          <span style={{ cursor: 'pointer', textDecoration: 'underline', fontSize: isMobile ? '14px' : '11px' }} onClick={handleLogoClick}>Authenticulture</span>
          <span style={{ fontSize: isMobile ? '9px' : '8px', color: 'rgba(255,255,255,0.65)', fontWeight: 700 }}>(BETA)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.md }}>
          {!isMobile && <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.7)' }}>Be real. Stay free.</span>}
          <button
            onClick={() => setCurrentView('notifications')}
            aria-label="Notifications"
            style={{
              position: 'relative',
              background: 'transparent',
              border: 'none',
              color: '#fff',
              cursor: 'pointer',
              fontSize: isMobile ? '26px' : '18px',
              lineHeight: 1,
              padding: isMobile ? SPACING.xs : 0,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            🔔
            {unreadNotifications > 0 && (
              <span style={{
                position: 'absolute', top: 0, right: 0,
                backgroundColor: COLORS.highlight, color: COLORS.bg,
                borderRadius: '50%', width: 20, height: 20,
                fontSize: '11px', fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                pointerEvents: 'none',
              }}>
                {unreadNotifications > 9 ? '9+' : unreadNotifications}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Desktop Header Panel */}
      {!isMobile && (
        <div style={{
          padding: SPACING.lg,
          backgroundColor: COLORS.panel,
          border: `2px outset ${COLORS.highlight}`,
          margin: SPACING.md,
          marginBottom: 0,
          flexShrink: 0,
        }}>
          <div style={{ marginBottom: SPACING.lg }}>
            <img
              src={logoWordmark}
              alt="Authenticulture"
              onClick={handleLogoClick}
              style={{ height: '56px', width: 'auto', display: 'block', marginBottom: SPACING.xs, cursor: 'pointer' }}
            />
            <p style={{ margin: 0, fontSize: '9px', color: COLORS.textMuted, letterSpacing: '0.05em' }}>
              Be real. Stay free.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: SPACING.sm }}>
            {navButton('Feed', 'home')}
            <div style={{ position: 'relative', display: 'grid' }}>
              {navButton('Inbox', 'inbox')}
              {unreadDMs > 0 && (
                <span style={{
                  position: 'absolute', top: -4, right: -4,
                  backgroundColor: COLORS.accent, color: '#fff',
                  borderRadius: '50%', width: 16, height: 16,
                  fontSize: '8px', fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  pointerEvents: 'none',
                }}>
                  {unreadDMs > 9 ? '9+' : unreadDMs}
                </span>
              )}
            </div>
            <button
              onClick={handleCreateClick}
              style={{
                background: COLORS.accent, color: '#fff',
                border: `2px outset ${COLORS.highlight}`, cursor: 'pointer',
                fontFamily: '"IBM Plex Mono", monospace', fontSize: '10px', fontWeight: 700,
                letterSpacing: '0.05em', padding: `${SPACING.sm} ${SPACING.lg}`,
              }}
            >
              + Create
            </button>
            <div style={{ position: 'relative', display: 'grid' }}>
              {navButton('Profile', 'profile')}
              <button
                onClick={e => { e.stopPropagation(); setCurrentView('pins'); }}
                style={{
                  position: 'absolute', top: 0, left: 0,
                  background: COLORS.accent, color: '#fff',
                  border: 'none', borderRadius: '0 0 3px 0',
                  padding: '2px 7px', cursor: 'pointer',
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: '9px', fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                ★ {tastemakerScore}
              </button>
            </div>
            {navButton('Settings', 'settings')}
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div style={{
        flex: 1,
        margin: isMobile ? 0 : SPACING.md,
        marginTop: isMobile ? 0 : SPACING.lg,
        paddingBottom: isMobile ? 78 : 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {currentView === 'home' && (
          <DiscoverFeed
            key={feedRefresh}
            profileCreated={profileCreated}
            onViewVideo={handleViewVideo}
            onNavigate={setCurrentView}
            onRequireAccount={() => setCurrentView('onboarding')}
          />
        )}

        {currentView === 'inbox' && (
          <Inbox
            onOpenDM={handleMessageUser}
            onOpenCommunity={handleOpenCommunity}
            onBrowseCommunities={() => setCurrentView('communities')}
          />
        )}

        {currentView === 'community-detail' && selectedCommunityId && (
          <CommunityDetail
            communityId={selectedCommunityId}
            onBack={() => setCurrentView('inbox')}
            onViewUserProfile={handleViewUserProfile}
            onViewVideo={handleViewVideo}
          />
        )}

        {currentView === 'onboarding' && (
          <Onboarding onComplete={() => { setProfileCreated(true); setCurrentView('home'); }} />
        )}

        {currentView === 'live' && <Live />}

        {currentView === 'videos' && (
          <>
            <Upload />
            <Feed onViewUserProfile={handleViewUserProfile} onViewTag={handleViewTag} />
          </>
        )}

        {currentView === 'tags' && (
          <Tags onBack={() => setCurrentView('home')} onViewUserProfile={handleViewUserProfile} onViewVideo={handleViewVideo} />
        )}

        {currentView === 'communities' && (
          <Communities onBack={() => setCurrentView('home')} onViewUserProfile={handleViewUserProfile} onViewVideo={handleViewVideo} />
        )}

        {currentView === 'video' && selectedVideoHash && (
          <VideoPlayer
            videoHash={selectedVideoHash}
            onBack={() => setCurrentView(previousView as any)}
            onViewUserProfile={handleViewUserProfile}
            onViewTag={handleViewTag}
          />
        )}

        {currentView === 'messages' && (
          <DirectMessages
            onBack={() => setCurrentView('home')}
            startConversationWith={startConversationWith ?? undefined}
          />
        )}

        {currentView === 'profile' && (
          <Profile onBack={() => setCurrentView('home')} onOpenSettings={() => setCurrentView('settings')} />
        )}

        {currentView === 'user-profile' && selectedUserId && (
          <UserProfileView userId={selectedUserId} onBack={() => setCurrentView('home')} onMessage={handleMessageUser} />
        )}

        {currentView === 'settings' && (
          <NetworkSettings
            onBack={() => setCurrentView('home')}
            onOpenLegal={() => setCurrentView('legal')}
            onOpenReports={() => setCurrentView('admin-reports')}
            isAdminUser={isAdmin(identity.publicKey)}
          />
        )}

        {currentView === 'legal' && (
          <Legal onBack={() => setCurrentView('settings')} />
        )}

        {currentView === 'admin-reports' && (
          <AdminReports onBack={() => setCurrentView('settings')} />
        )}

        {currentView === 'notifications' && (
          <Notifications onBack={() => setCurrentView('home')} onNavigate={handleNotificationNavigate} />
        )}

        {currentView === 'posts' && <PostFeed />}
        {currentView === 'events' && <EventFeed />}
        {currentView === 'pins' && (
          <TastemakerPins onBack={() => setCurrentView('profile')} onPlayVideo={handleViewVideo} />
        )}
      </div>
      {isMobile && bottomNav}
      {showCreate && (
        <CreateSheet
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); setCurrentView('home'); setFeedRefresh(n => n + 1); }}
        />
      )}
    </div>
  );
}

export default App;
